import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { themeApi, type TenantTheme } from "@/lib/api.js";
import { I18nEditor } from "@/components/I18nEditor.js";
import { MediaPickerField } from "@/components/MediaPickerField.js";
import type { Language } from "@templestay/i18n";

type I18nValue = Record<Language, Record<string, string>>;

const LANGS: Language[] = ["ko", "en", "zh", "ja"];
const EMPTY_I18N: I18nValue = { ko: {}, en: {}, zh: {}, ja: {} };

const TITLE_FIELDS = [{ key: "html_title", label: "브라우저 탭 제목" }];
const FOOTER_FIELDS = [
  { key: "footer_name", label: "사찰명" },
  { key: "footer_address", label: "주소" },
  { key: "footer_phone", label: "전화번호" },
  { key: "footer_copyright", label: "저작권" },
];
const SPLASH_FIELDS = [
  { key: "splash_headline", label: "헤드라인" },
  { key: "splash_subheadline", label: "서브헤드라인" },
];

function themeToI18n(theme: TenantTheme): I18nValue {
  const result: I18nValue = { ko: {}, en: {}, zh: {}, ja: {} };
  for (const lang of LANGS) {
    result[lang] = {
      html_title: theme.html_title?.[lang] ?? "",
      footer_name: theme.footer?.name?.[lang] ?? "",
      footer_address: theme.footer?.address?.[lang] ?? "",
      footer_phone: theme.footer?.phone?.[lang] ?? "",
      footer_copyright: theme.footer?.copyright?.[lang] ?? "",
      splash_headline: theme.splash?.headline?.[lang] ?? theme.splash?.name?.[lang] ?? "",
      splash_subheadline: theme.splash?.subheadline?.[lang] ?? "",
    };
  }
  return result;
}

function buildTheme(
  i18n: I18nValue,
  faviconUrl: string,
  logoUrl: string,
  splashBgColor: string,
  splashTextColor: string,
  splashDotColor: string
): TenantTheme {
  const extract = (key: string): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const lang of LANGS) {
      if (i18n[lang][key]?.trim()) out[lang] = i18n[lang][key];
    }
    return out;
  };
  return {
    ...(faviconUrl.trim() && { favicon_url: faviconUrl.trim() }),
    ...(logoUrl.trim() && { logo_url: logoUrl.trim() }),
    ...(Object.keys(extract("html_title")).length && { html_title: extract("html_title") }),
    footer: {
      ...(Object.keys(extract("footer_name")).length && { name: extract("footer_name") }),
      ...(Object.keys(extract("footer_address")).length && { address: extract("footer_address") }),
      ...(Object.keys(extract("footer_phone")).length && { phone: extract("footer_phone") }),
      ...(Object.keys(extract("footer_copyright")).length && { copyright: extract("footer_copyright") }),
    },
    splash: {
      ...(Object.keys(extract("splash_headline")).length && { headline: extract("splash_headline") }),
      ...(Object.keys(extract("splash_subheadline")).length && { subheadline: extract("splash_subheadline") }),
      ...(splashBgColor.trim() && { bg_color: splashBgColor.trim() }),
      ...(splashTextColor.trim() && { text_color: splashTextColor.trim() }),
      ...(splashDotColor.trim() && { dot_color: splashDotColor.trim() }),
    },
  };
}

export function ThemeSettingsPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const qc = useQueryClient();
  const [i18n, setI18n] = useState<I18nValue>(EMPTY_I18N);
  const [faviconUrl, setFaviconUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [splashBgColor, setSplashBgColor] = useState("");
  const [splashTextColor, setSplashTextColor] = useState("");
  const [splashDotColor, setSplashDotColor] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-theme", tenantSlug],
    queryFn: () => themeApi.get(),
  });

  useEffect(() => {
    if (data) {
      setI18n(themeToI18n(data));
      setFaviconUrl(data.favicon_url ?? "");
      setLogoUrl(data.logo_url ?? "");
      setSplashBgColor(data.splash?.bg_color ?? "");
      setSplashTextColor(data.splash?.text_color ?? "");
      setSplashDotColor(data.splash?.dot_color ?? "");
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => themeApi.update(buildTheme(i18n, faviconUrl, logoUrl, splashBgColor, splashTextColor, splashDotColor)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-theme", tenantSlug] });
      alert("저장되었습니다.");
    },
    onError: (err) => alert(err instanceof Error ? err.message : "저장에 실패했습니다."),
  });

  if (isLoading) {
    return (
      <div className="flex-1 overflow-y-auto p-8">
        <div className="flex items-center justify-center py-24">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">테마 설정</h1>
        <p className="mt-1 text-sm text-gray-500">사찰 사이트 브랜딩을 설정합니다</p>
      </div>

      <div className="mx-auto max-w-2xl space-y-6">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-4 text-sm font-bold text-gray-700">아이콘 / 로고</h2>
          <div className="space-y-4">
            <MediaPickerField label="Favicon" value={faviconUrl} onChange={setFaviconUrl} />
            <MediaPickerField label="로고" value={logoUrl} onChange={setLogoUrl} />
          </div>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-4 text-sm font-bold text-gray-700">브라우저 탭 제목</h2>
          <I18nEditor value={i18n} fields={TITLE_FIELDS} onChange={setI18n} defaultLang="ko" />
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-4 text-sm font-bold text-gray-700">푸터</h2>
          <I18nEditor value={i18n} fields={FOOTER_FIELDS} onChange={setI18n} defaultLang="ko" />
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-4 text-sm font-bold text-gray-700">스플래시 화면</h2>
          <I18nEditor value={i18n} fields={SPLASH_FIELDS} onChange={setI18n} defaultLang="ko" />
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1 block text-xs text-gray-500">배경 색상</label>
              <div className="flex gap-2">
                <input type="color" value={splashBgColor || "#f5f4ee"} onChange={(e) => setSplashBgColor(e.target.value)} className="h-9 w-10 cursor-pointer rounded border border-gray-200 p-0.5" />
                <input type="text" value={splashBgColor} onChange={(e) => setSplashBgColor(e.target.value)} placeholder="#f5f4ee" className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-xs font-mono outline-none focus:border-brand-400" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">텍스트 색상</label>
              <div className="flex gap-2">
                <input type="color" value={splashTextColor || "#1c1917"} onChange={(e) => setSplashTextColor(e.target.value)} className="h-9 w-10 cursor-pointer rounded border border-gray-200 p-0.5" />
                <input type="text" value={splashTextColor} onChange={(e) => setSplashTextColor(e.target.value)} placeholder="#1c1917" className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-xs font-mono outline-none focus:border-brand-400" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">점 색상</label>
              <div className="flex gap-2">
                <input type="color" value={splashDotColor || "#4c4a31"} onChange={(e) => setSplashDotColor(e.target.value)} className="h-9 w-10 cursor-pointer rounded border border-gray-200 p-0.5" />
                <input type="text" value={splashDotColor} onChange={(e) => setSplashDotColor(e.target.value)} placeholder="#4c4a31" className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-xs font-mono outline-none focus:border-brand-400" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end pb-8">
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {saveMutation.isPending ? "저장 중..." : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}
